import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import type {
  IAReadiness,
  AssetFull,
  CaseLiability,
  SwornInventoryMeta,
  AssetSupplemental,
} from '../types';

// ---------------------------------------------------------------------------
// Inventory Date
// ---------------------------------------------------------------------------

export function useInventoryDate(caseId: string | undefined) {
  return useQuery<{ inventory_date: string | null; separation_date: string | null; filing_date: string | null }>({
    queryKey: ['inventory-date', caseId],
    queryFn: async () => {
      const { data } = await api.get(`/cases/${caseId}/inventory-date`);
      return data;
    },
    enabled: !!caseId,
  });
}

export function useSetInventoryDate(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (inventoryDate: string | null) => {
      const { data } = await api.patch(`/cases/${caseId}/inventory-date`, {
        inventory_date: inventoryDate,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory-date', caseId] });
    },
  });
}

// ---------------------------------------------------------------------------
// Document Preview
// ---------------------------------------------------------------------------

export interface IAPreviewSection {
  title: string;
  items: Array<{ name?: string; creditor_name?: string; asset_type?: string; estimated_value?: number | null; balance?: number | null; property_status?: string; liability_type?: string; value_known?: boolean; account_last4?: string | null; source_file?: string | null; as_of?: string | null; source_citation?: string | null }>;
  total: number;
}

export interface IAPreview {
  case_info: {
    client_name: string | null;
    opposing_party_name: string | null;
    cause_number: string | null;
    court: string | null;
    county: string | null;
    inventory_date: string | null;
    firm_name: string | null;
    attorney_name: string | null;
  };
  sections: IAPreviewSection[];
  summary: {
    net_community_estate: number;
    net_separate_estate: number;
  };
  warnings?: string[];
}

export function useIAPreview(caseId: string | undefined) {
  return useQuery<IAPreview>({
    queryKey: ['ia-preview', caseId],
    queryFn: async () => {
      const { data } = await api.get(`/cases/${caseId}/ia-preview`);
      return data;
    },
    enabled: !!caseId,
  });
}

// ---------------------------------------------------------------------------
// Readiness
// ---------------------------------------------------------------------------

export function useIAReadiness(caseId: string | undefined) {
  return useQuery<IAReadiness>({
    queryKey: ['ia-readiness', caseId],
    queryFn: async () => {
      const { data } = await api.get(`/cases/${caseId}/ia-readiness`);
      return data;
    },
    enabled: !!caseId,
  });
}

// ---------------------------------------------------------------------------
// Assets (with supplemental fields)
// ---------------------------------------------------------------------------

export function useAssetsWithSupplemental(caseId: string | undefined) {
  return useQuery<AssetFull[]>({
    queryKey: ['assets-full', caseId],
    queryFn: async () => {
      const { data } = await api.get(`/cases/${caseId}/assets`);
      return data as AssetFull[];
    },
    enabled: !!caseId,
  });
}

export function useUpdateAssetSupplemental(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ assetId, updates }: { assetId: number; updates: Partial<AssetSupplemental> }) => {
      const { data } = await api.patch(`/cases/${caseId}/assets/${assetId}`, updates);
      return data as AssetFull;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets-full', caseId] });
      queryClient.invalidateQueries({ queryKey: ['ia-readiness', caseId] });
    },
  });
}

// ---------------------------------------------------------------------------
// Liabilities
// ---------------------------------------------------------------------------

export function useLiabilities(caseId: string | undefined) {
  return useQuery<CaseLiability[]>({
    queryKey: ['liabilities', caseId],
    queryFn: async () => {
      const { data } = await api.get(`/cases/${caseId}/liabilities`);
      return data as CaseLiability[];
    },
    enabled: !!caseId,
  });
}

export function useCreateLiability(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: Omit<CaseLiability, 'id' | 'case_id' | 'created_at' | 'updated_at'>) => {
      const { data } = await api.post(`/cases/${caseId}/liabilities`, body);
      return data as CaseLiability;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['liabilities', caseId] });
      queryClient.invalidateQueries({ queryKey: ['ia-readiness', caseId] });
    },
  });
}

export function useUpdateLiability(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }: { id: number; updates: Partial<CaseLiability> }) => {
      const { data } = await api.patch(`/cases/${caseId}/liabilities/${id}`, updates);
      return data as CaseLiability;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['liabilities', caseId] });
    },
  });
}

export function useDeleteLiability(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/cases/${caseId}/liabilities/${id}`);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['liabilities', caseId] });
      queryClient.invalidateQueries({ queryKey: ['ia-readiness', caseId] });
    },
  });
}

// ---------------------------------------------------------------------------
// Sworn Inventories
// ---------------------------------------------------------------------------

export function useSwornInventories(caseId: string | undefined) {
  return useQuery<{ inventories: SwornInventoryMeta[] }>({
    queryKey: ['sworn-inventories', caseId],
    queryFn: async () => {
      const { data } = await api.get(`/cases/${caseId}/sworn-inventories`);
      return data;
    },
    enabled: !!caseId,
  });
}

export function useGenerateSwornInventory(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post(`/cases/${caseId}/sworn-inventory`);
      return data as SwornInventoryMeta;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sworn-inventories', caseId] });
    },
  });
}

export function useDeleteSwornInventory(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (invId: number) => {
      await api.delete(`/cases/${caseId}/sworn-inventories/${invId}`);
      return invId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sworn-inventories', caseId] });
    },
  });
}

// ---------------------------------------------------------------------------
// I&A Checklist — per-category "Confirmed None" status
// ---------------------------------------------------------------------------

export interface IaChecklistCategory {
  key: string;
  number: string;
  title: string;
  heading_group: string;
  heading_label: string;
  item_count: number;
  has_data: boolean;
  confirmed_none: boolean;
}

export function useIaChecklist(caseId: string | undefined) {
  return useQuery<{ categories: IaChecklistCategory[] }>({
    queryKey: ['ia-checklist', caseId],
    queryFn: async () => {
      const { data } = await api.get(`/cases/${caseId}/ina-checklist`);
      return data as { categories: IaChecklistCategory[] };
    },
    enabled: !!caseId,
  });
}

export function useUpdateIaChecklist(caseId: string) {
  const queryClient = useQueryClient();
  const key = ['ia-checklist', caseId];
  return useMutation({
    mutationFn: async ({ category_key, confirmed }: { category_key: string; confirmed: boolean }) => {
      const { data } = await api.patch(`/cases/${caseId}/ina-checklist`, { category_key, confirmed });
      return data as { confirmed_none: string[] };
    },
    // Optimistic: flip the one toggled box instantly; the rest of the list stays live.
    onMutate: async ({ category_key, confirmed }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<{ categories: IaChecklistCategory[] }>(key);
      if (previous) {
        queryClient.setQueryData(key, {
          categories: previous.categories.map(c =>
            c.key === category_key ? { ...c, confirmed_none: confirmed } : c),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      // Roll back so a failed save never leaves a phantom confirmation on screen.
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key });
      queryClient.invalidateQueries({ queryKey: ['ia-preview', caseId] });
    },
  });
}
